
import {
  computed, defineComponent, PropType, ref, SetupContext, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  SpmTableFilter, StringMap,
} from '@/types';
import BaseItemList from '@/components/fields/BaseItemList.vue';
import ButtonGroup from './ButtonGroup.vue';
import type { PickerData } from '@/types/flow-types';

export interface ItemConfig {
    mainLabel: string;
    buttonLabel: string;
    operatorLabel: string;
    name: string;
    fields: StringMap;
    defaultFilters: SpmTableFilter[];
    format: Function;
    showOperator?: boolean;
    enableVariants?: boolean;
}

export default defineComponent({
  name: 'ItemList',

  components: {
    BaseItemList,
    ButtonGroup,
  },

  props: {
    modelValue: {
      type: Object as PropType<{ selection: PickerData}>,
      required: true,
    },

    config: {
      type: Object as PropType<ItemConfig>,
      required: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }: SetupContext) {
    const { t } = useI18n();

    // true by default
    const showOperator = ref(props.config.showOperator !== undefined ? props.config.showOperator : true);

    const selected = ref<any[]>(props.modelValue ? props.modelValue.selection.selected : []);
    const operator = ref(props.modelValue ? props.modelValue.selection.operator : 'and');

    const logicalOperators = [
      { value: 'and', label: t('automatedScenarios.fields.allItems') },
      { value: 'or', label: t('automatedScenarios.fields.atLeastOne') },
    ];

    const isVariantSelection = (item: any): boolean => (
      item?._isVariant === 'true'
      || item?.isVariant === 'true'
      || item.id_shop_product_attribute !== undefined
      || item.id_product_attribute !== undefined
      || item.id_combination !== undefined
    );

    const selectedProductIds = computed(() => selected.value
      .filter((item) => !isVariantSelection(item))
      .map((item) => String(item.spmId ?? ''))
      .filter((id: string) => `${id}`.trim() !== '')
      .join(','));

    const selectedVariantIds = computed(() => selected.value
      .filter((item) => isVariantSelection(item))
      .map((item: any) => String(
        item.id_shop_product_attribute ?? item._variantId ?? '',
      ))
      .filter((id: string) => id !== '')
      .join(','));

    const formatSelectedItem = (item: any) => {
      if (isVariantSelection(item)) {
        return {
          id: String(item.id_product ?? item.id ?? ''),
          spmId: String(item.id_shop_product ?? item.spmId ?? ''),
          name: String(item.name ?? item._combinationName ?? ''),
          combinations: item.combinations ?? {},
          _isVariant: 'true',
          id_shop_product_attribute: String(
            item.id_shop_product_attribute ?? item._variantId ?? 0,
          ),
          id_product_attribute: String(
            item.id_product_attribute ?? item._variantElementId ?? 0,
          ),
          id_combination: String(
            item.id_combination ?? item._variantElementId ?? 0,
          ),
        };
      }

      return props.config.format(item);
    };

    watch([selected, operator], () => {
      emit('update:modelValue', {
        selection: {
          operator: operator.value,
          selected: selected.value,
        },
      });
    });

    return {
      selected,
      logicalOperators,
      operator,
      showOperator,
      selectedProductIds,
      selectedVariantIds,
      formatSelectedItem,
    };
  },

});
